import { useEffect, useState } from 'react';
import { useFontDisplayName } from '../../controls/GroupPreview/TextPreview/useFontDisplayName';
import { useClient } from '../../common/atomic/hooks';
import { processStyleMetadataForLobby } from '../../common/lobby/processStyleMetadataForLobby';
import type { GlobalDesignStyleGroup } from '../../types';
import {
  normalizeUnit,
  TEXT_PROPERTY_CONFIG,
  getFontRawValue,
} from '../utils/textUtils';

export interface TextLobbyData {
  fontFamily: string | undefined;
  fontColor: string | undefined;
  fontSize: string | undefined;
  fontWeight: string | undefined;
  fontStyle: string | undefined;
  fontDisplayName: string | undefined;
  textStyleLabel: string | undefined;
  emptyPreviewLabel: string;
  isMixedFontColor: boolean;
  isMixedFontFamily: boolean;
  isMixedFontSize: boolean;
  isMixedFontWeight: boolean;
  isMixedFontStyle: boolean;
}

export function useTextLobbyData(group: GlobalDesignStyleGroup): TextLobbyData {
  const client = useClient(group);

  const processedStyleMetadata = processStyleMetadataForLobby(
    group.engine,
    TEXT_PROPERTY_CONFIG,
  );

  const fontFamily = processedStyleMetadata.fontFamily[0];
  const fontColor = processedStyleMetadata.color[0];
  const fontSize = normalizeUnit(processedStyleMetadata.fontSize[0]);
  const fontWeight = processedStyleMetadata.fontWeight[0];
  const fontStyle = processedStyleMetadata.fontStyle[0];

  const fontDisplayName = useFontDisplayName(client, fontFamily || '');

  // Resolve the text style display name (e.g. "Heading 1") from the engine's
  // font theme value, using getFontRawValue from shared utils.
  const engineValues = group.engine?.getAllValues();
  const fontThemeValue = engineValues
    ? getFontRawValue(engineValues)
    : undefined;
  const varKey = fontThemeValue?.match(/var\(--([^)]+)\)/)?.[1];

  const [textStyleLabel, setTextStyleLabel] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!varKey || !client) {
      setTextStyleLabel(undefined);
      return;
    }
    let cancelled = false;
    client.info
      .getThemeCustomProperties({ keys: [varKey] })
      .then((fonts) => {
        if (!cancelled) {
          const font = fonts[varKey];
          if ('displayName' in font) {
            setTextStyleLabel(font.displayName ?? undefined);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTextStyleLabel(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, varKey]);

  const emptyPreviewLabel =
    fontFamily || group.displayName.group || group.displayName.component;

  return {
    fontFamily,
    fontColor,
    fontSize,
    fontWeight,
    fontStyle,
    fontDisplayName,
    textStyleLabel,
    emptyPreviewLabel: emptyPreviewLabel || '',
    isMixedFontColor: processedStyleMetadata.isMixedFontColor,
    isMixedFontFamily: processedStyleMetadata.isMixedFontFamily,
    isMixedFontSize: processedStyleMetadata.isMixedFontSize,
    isMixedFontWeight: processedStyleMetadata.isMixedFontWeight,
    isMixedFontStyle: processedStyleMetadata.isMixedFontStyle,
  };
}
